import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

// Converte colore HEX in RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

// Genera palette colori da un colore base
const generatePalette = (hex) => {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  
  // Genera varianti (semplificate)
  return {
    50: `${Math.min(rgb.r + 100, 255)} ${Math.min(rgb.g + 100, 255)} ${Math.min(rgb.b + 100, 255)}`,
    100: `${Math.min(rgb.r + 80, 255)} ${Math.min(rgb.g + 80, 255)} ${Math.min(rgb.b + 80, 255)}`,
    200: `${Math.min(rgb.r + 60, 255)} ${Math.min(rgb.g + 60, 255)} ${Math.min(rgb.b + 60, 255)}`,
    300: `${Math.min(rgb.r + 40, 255)} ${Math.min(rgb.g + 40, 255)} ${Math.min(rgb.b + 40, 255)}`,
    400: `${Math.min(rgb.r + 20, 255)} ${Math.min(rgb.g + 20, 255)} ${Math.min(rgb.b + 20, 255)}`,
    500: `${rgb.r} ${rgb.g} ${rgb.b}`,
    600: `${Math.max(rgb.r - 20, 0)} ${Math.max(rgb.g - 20, 0)} ${Math.max(rgb.b - 20, 0)}`,
    700: `${Math.max(rgb.r - 40, 0)} ${Math.max(rgb.g - 40, 0)} ${Math.max(rgb.b - 40, 0)}`,
    800: `${Math.max(rgb.r - 60, 0)} ${Math.max(rgb.g - 60, 0)} ${Math.max(rgb.b - 60, 0)}`,
    900: `${Math.max(rgb.r - 80, 0)} ${Math.max(rgb.g - 80, 0)} ${Math.max(rgb.b - 80, 0)}`,
  }
}

// Applica colori CSS variables
const applyColors = (primaryHex, secondaryHex) => {
  const root = document.documentElement
  
  if (primaryHex) {
    const primary = generatePalette(primaryHex)
    if (primary) {
      Object.entries(primary).forEach(([shade, rgb]) => {
        root.style.setProperty(`--color-primary-${shade}`, rgb)
      })
    }
  }
  
  if (secondaryHex) {
    const secondary = generatePalette(secondaryHex)
    if (secondary) {
      Object.entries(secondary).forEach(([shade, rgb]) => {
        root.style.setProperty(`--color-secondary-${shade}`, rgb)
      })
    }
  }
}

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode')
    return saved ? JSON.parse(saved) : false
  })
  
  const [tenantColors, setTenantColors] = useState({
    primary: null,
    secondary: null
  })
  
  // Applica dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode))
  }, [darkMode])
  
  // Applica colori tenant
  useEffect(() => {
    if (tenantColors.primary || tenantColors.secondary) {
      applyColors(tenantColors.primary, tenantColors.secondary)
    }
  }, [tenantColors])
  
  const toggleDarkMode = () => setDarkMode(!darkMode)
  
  const setColors = (primary, secondary) => {
    setTenantColors({ primary, secondary })
  }
  
  return (
    <ThemeContext.Provider value={{ 
      darkMode, 
      toggleDarkMode, 
      setColors,
      tenantColors 
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
